import { useEffect, useMemo, useRef, useState } from "react"
import { ChevronDownIcon, DownloadIcon } from "@radix-ui/react-icons"

import { type LicenseLogo } from "@/lib/getLicenseLogos"
import {
  buildLogoBarLayout,
  ICON_SIZE,
  GAP,
  resolveLogoVariant,
  type LayoutCell
} from "@/lib/logoBarLayout"
import { type AssetOption, type DownloadFormat } from "@/hooks/useLicenseAssets"
import { LicenseLogo } from "../common/LicenseLogo"
import { Button } from "../ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu"

type LicenseLogoBarProps = {
  logos: LicenseLogo[]
  licenseName: string
  licenseCode?: string
  downloadOptions?: AssetOption[]
  onDownload?: (key: DownloadFormat) => void | Promise<void>
}

export const LicenseLogoBar = ({
  logos,
  licenseName,
  licenseCode,
  downloadOptions = [],
  onDownload
}: LicenseLogoBarProps) => {
  const DIVIDER_PADDING = 6
  const DIVIDER_LINE = 1
  const DIVIDER_SPACE = DIVIDER_LINE + DIVIDER_PADDING * 2

  if (!logos.length) return null
  const showVariant = licenseCode && licenseCode !== "std"

  const layout = useMemo(() => buildLogoBarLayout(logos), [logos])

  const hasDivider = layout.sharingWidth > 0 && layout.columns > layout.sharingWidth
  const dividerSpace = hasDivider ? DIVIDER_SPACE : 0
  const dividerOffset = hasDivider
    ? layout.sharingWidth * ICON_SIZE + Math.max(0, layout.sharingWidth - 1) * GAP + DIVIDER_PADDING
    : null

  const targetWidth = useMemo(() => {
    if (!layout.columns) return 0
    return (
      layout.columns * ICON_SIZE +
      Math.max(0, layout.columns - 1) * GAP +
      dividerSpace
    )
  }, [layout.columns, dividerSpace])

  const targetHeight = useMemo(() => {
    if (!layout.rows) return 0
    return layout.rows * ICON_SIZE + Math.max(0, layout.rows - 1) * GAP
  }, [layout.rows])

  const sharingCellCount = useMemo(
    () =>
      layout.cells.filter(
        cell => !cell.isGroup && cell.col < layout.sharingWidth
      ).length,
    [layout]
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)

  useEffect(() => {
    const el = containerRef.current
    if (!el || !targetWidth) return

    const updateScale = (width: number) => {
      const next = width ? Math.min(1, width / targetWidth) : 1
      setScale(next)
    }

    updateScale(el.clientWidth)

    const observer = new ResizeObserver(entries => {
      const entry = entries[0]
      if (entry) updateScale(entry.contentRect.width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [targetWidth, logos.length])

  const renderCell = (cell: LayoutCell) => {
    const key = `${cell.row}-${cell.col}-${cell.logos
      .map(item => item.key)
      .join("-")}`
    const columnStart =
      cell.col +
      1 +
      (hasDivider && layout.sharingWidth && cell.col >= layout.sharingWidth ? 1 : 0)

    if (!cell.isGroup) {
      const shouldCenterSharing =
        sharingCellCount === 3 &&
        layout.sharingWidth === 2 &&
        cell.col < layout.sharingWidth &&
        cell.row === 1

      return (
        <div
          key={key}
          style={{
            gridColumnStart: columnStart,
            gridRowStart: cell.row + 1,
            ...(shouldCenterSharing
              ? {
                  gridColumnEnd: columnStart + layout.sharingWidth,
                  justifySelf: "center"
                }
              : null)
          }}
        >
            <LicenseLogo
              logo={cell.logos[0].key}
              title={cell.logos[0].title}
              variant={resolveLogoVariant(cell.logos[0])}
              className="h-14 w-14"
            />
        </div>
      )
    }

    if (cell.logos.length === 1) {
      const logo = cell.logos[0]
      return (
        <div
          key={key}
          className="flex items-start justify-center"
          style={{
            gridColumnStart: columnStart,
            gridRowStart: cell.row + 1,
            width: `${ICON_SIZE}px`,
            height: `${ICON_SIZE}px`
          }}
        >
            <LicenseLogo
              logo={logo.key}
              title={logo.title}
              variant={resolveLogoVariant(logo)}
              className="h-7 w-7"
            />
        </div>
      )
    }

    if (cell.logos.length === 2) {
      return (
        <div
          key={key}
          className="grid"
          style={{
            gridColumnStart: columnStart,
            gridRowStart: cell.row + 1,
            width: `${ICON_SIZE}px`,
            height: `${ICON_SIZE}px`,
            gridTemplateColumns: `repeat(2, ${ICON_SIZE / 2}px)`,
            gridTemplateRows: `${ICON_SIZE / 2}px`,
            gap: 0,
            justifyContent: "center",
            justifyItems: "center",
            alignItems: "start"
          }}
        >
          {cell.logos.map(logo => (
            <LicenseLogo
              key={logo.key}
              logo={logo.key}
              title={logo.title}
              variant={resolveLogoVariant(logo)}
              className="h-7 w-7"
            />
          ))}
        </div>
      )
    }

    if (cell.logos.length === 3) {
      return (
        <div
          key={key}
          className="grid"
          style={{
            gridColumnStart: columnStart,
            gridRowStart: cell.row + 1,
            width: `${ICON_SIZE}px`,
            height: `${ICON_SIZE}px`,
            gridTemplateColumns: `repeat(2, ${ICON_SIZE / 2}px)`,
            gridTemplateRows: `repeat(2, ${ICON_SIZE / 2}px)`,
            gap: 0,
            justifyItems: "center",
            alignItems: "start",
            alignContent: "start"
          }}
        >
          {cell.logos.map((logo, index) => (
            <div
              key={logo.key}
              style={
                index === 2
                  ? { gridColumn: "1 / span 2", justifySelf: "center" }
                  : undefined
              }
            >
              <LicenseLogo
                logo={logo.key}
                title={logo.title}
                variant={resolveLogoVariant(logo)}
                className="h-7 w-7"
              />
            </div>
          ))}
        </div>
      )
    }

    return (
      <div
        key={key}
        className="grid"
        style={{
          gridColumnStart: columnStart,
          gridRowStart: cell.row + 1,
          width: `${ICON_SIZE}px`,
          height: `${ICON_SIZE}px`,
          gridTemplateColumns: `repeat(2, ${ICON_SIZE / 2}px)`,
          gridTemplateRows: `repeat(2, ${ICON_SIZE / 2}px)`,
          gap: 0,
          justifyItems: "center",
          alignItems: "start"
        }}
      >
        {cell.logos.map(logo => (
          <LicenseLogo
            key={logo.key}
            logo={logo.key}
            title={logo.title}
            variant={resolveLogoVariant(logo)}
            className="h-7 w-7"
          />
        ))}
      </div>
    )
  }

  const showDownload = downloadOptions.length > 0 && typeof onDownload === "function"

  return (
    <div className="rounded-2xl bg-gray-100 px-4 py-4 w-full mt-2 max-w-[492px]">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold leading-tight text-slate-700">{licenseName}</div>
        {showDownload ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="h-7 gap-1 rounded-full px-2 text-[11px] font-semibold text-slate-600 shadow-none hover:bg-white"
              >
                <DownloadIcon className="size-3" />
                Download
                <ChevronDownIcon className="size-3 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {downloadOptions.map(option => (
                <DropdownMenuItem
                  key={option.key}
                  onSelect={event => {
                    event.preventDefault()
                    onDownload(option.key)
                  }}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      <div ref={containerRef} className="mt-2 w-full">
        <div
          className="relative grid"
          style={{
            width: `${targetWidth}px`,
            gridTemplateColumns: hasDivider
              ? `repeat(${layout.sharingWidth}, ${ICON_SIZE}px) ${DIVIDER_SPACE}px repeat(${layout.columns - layout.sharingWidth}, ${ICON_SIZE}px)`
              : `repeat(${layout.columns}, ${ICON_SIZE}px)`,
            gridTemplateRows: `repeat(${layout.rows}, ${ICON_SIZE}px)`,
            gap: `${GAP}px`,
            zoom: scale,
            transformOrigin: "left center"
          }}
        >
          {hasDivider && dividerOffset !== null ? (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute top-0"
              style={{
                left: `${dividerOffset}px`,
                width: `${DIVIDER_LINE}px`,
                height: `${targetHeight}px`,
                backgroundColor: "#cbd5e1"
              }}
            />
          ) : null}
          {layout.cells.map(renderCell)}
        </div>
      </div>
        {/* {showVariant ? (
          <div className="mt-1 text-[11px] text-slate-500">
            Variant: {licenseCode}
          </div>
        ) : null} */}

    </div>
  )
}
